import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, BarChart, Bar } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Section } from "./AiHtml";

const KEYS = [["seo", "SEO", "#FF6B00"], ["aeo", "AEO", "#3B82F6"], ["geo", "GEO", "#10B981"], ["social", "Social", "#A855F7"]];
const tip = { contentStyle: { background: "#0B1120", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 }, labelStyle: { color: "#94A3B8" } };

export const historyOf = (brand) => {
  if (brand?.auditHistory?.length) return brand.auditHistory;
  const a = brand?.auditReport;
  return a ? [{ date: a.generatedAt, seo: a.seo_score, aeo: a.aeo_score, geo: a.geo_score, social: a.social_score }] : [];
};

const Delta = ({ v }) => {
  const Icon = v > 0 ? TrendingUp : v < 0 ? TrendingDown : Minus;
  const c = v > 0 ? "text-emerald-400" : v < 0 ? "text-red-400" : "text-slate-500";
  return <span className={`inline-flex items-center gap-1 font-mono text-sm ${c}`}><Icon size={14} />{v > 0 ? "+" : ""}{v}</span>;
};

export const AuditComparison = ({ brand }) => {
  const hist = historyOf(brand);
  if (!hist.length) return null;
  const first = hist[0];
  const last = hist[hist.length - 1];
  const data = hist.map((h, i) => ({ ...h, label: `#${i + 1} ${new Date(h.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` }));
  const bars = KEYS.map(([k, l]) => ({ name: l, Before: first[k], After: last[k] }));
  return (
    <Section title="Audit comparison" testId="audit-comparison" right={<span className="badge-o">{hist.length} audit{hist.length > 1 ? "s" : ""}</span>}>
      {hist.length < 2 ? (
        <p className="text-sm text-slate-400" data-testid="audit-comparison-empty">Run the audit again later to see your before-and-after progress. Baseline saved on {new Date(first.date).toLocaleDateString()}.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {KEYS.map(([k, l]) => (
              <div key={k} className="rounded-lg bg-slate-900 border border-slate-800 p-3" data-testid={`audit-delta-${k}`}>
                <p className="label-mono">{l}</p>
                <p className="mt-1 font-display text-xl font-bold">{first[k]} <span className="text-slate-600">→</span> {last[k]}</p>
                <Delta v={last[k] - first[k]} />
              </div>
            ))}
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="h-64" data-testid="audit-before-after-chart">
              <p className="text-xs text-slate-500 mb-2">First audit vs latest audit</p>
              <ResponsiveContainer><BarChart data={bars}><CartesianGrid stroke="#1E293B" vertical={false} /><XAxis dataKey="name" stroke="#64748B" fontSize={12} /><YAxis domain={[0, 100]} stroke="#64748B" fontSize={12} /><Tooltip {...tip} cursor={{ fill: "#1E293B55" }} /><Legend wrapperStyle={{ fontSize: 12 }} /><Bar dataKey="Before" fill="#475569" radius={[4, 4, 0, 0]} /><Bar dataKey="After" fill="#FF6B00" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
            </div>
            <div className="h-64" data-testid="audit-trend-chart">
              <p className="text-xs text-slate-500 mb-2">Score trend over time</p>
              <ResponsiveContainer><LineChart data={data}><CartesianGrid stroke="#1E293B" vertical={false} /><XAxis dataKey="label" stroke="#64748B" fontSize={11} /><YAxis domain={[0, 100]} stroke="#64748B" fontSize={12} /><Tooltip {...tip} /><Legend wrapperStyle={{ fontSize: 12 }} />{KEYS.map(([k, l, c]) => <Line key={k} type="monotone" dataKey={k} name={l} stroke={c} strokeWidth={2} dot={{ r: 3 }} />)}</LineChart></ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </Section>
  );
};
